import { useState, useEffect, useCallback } from 'react';

export interface TourStep {
  /** CSS selector for the target element to highlight */
  target: string;
  /** Title of the tour step */
  title: string;
  /** Description/content of the tour step */
  content: string;
  /** Placement of the tooltip relative to the target */
  placement?: 'top' | 'bottom' | 'left' | 'right';
}

const TOUR_STORAGE_KEY = 'bnm-onboarding-tour-completed';

const DEFAULT_STEPS: TourStep[] = [
  {
    target: '[data-tour="sidebar"]',
    title: 'Navigation Sidebar',
    content: 'Use the sidebar to navigate between modules — tasks, clients, documents, and more.',
    placement: 'right',
  },
  {
    target: '[data-tour="dashboard"]',
    title: 'Dashboard Overview',
    content: 'Your dashboard shows key metrics, recent activity, and quick actions at a glance.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="tasks"]',
    title: 'Task Management',
    content: 'Create, assign, and track tasks across your team. Use the Kanban board for visual workflow management.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="clients"]',
    title: 'Client Management',
    content: 'Manage all your clients, their contacts, and associated documents in one place.',
    placement: 'bottom',
  },
  {
    target: '[data-tour="search"]',
    title: 'Quick Search',
    content: 'Press Ctrl+K (or Cmd+K) to quickly search across all your data — clients, tasks, and documents.',
    placement: 'bottom',
  },
];

export function useOnboardingTour(customSteps?: TourStep[]) {
  const steps = customSteps ?? DEFAULT_STEPS;
  const [isActive, setIsActive] = useState(false);
  const [currentStep, setCurrentStep] = useState(0);

  // Check if tour should auto-start for new users
  useEffect(() => {
    try {
      const completed = localStorage.getItem(TOUR_STORAGE_KEY);
      if (!completed) {
        // Delay to let the UI render first
        const timer = setTimeout(() => setIsActive(true), 1500);
        return () => clearTimeout(timer);
      }
    } catch {
      // localStorage unavailable — skip tour
    }
  }, []);

  const next = useCallback(() => {
    if (currentStep < steps.length - 1) {
      setCurrentStep((s) => s + 1);
    } else {
      // Tour complete
      setIsActive(false);
      setCurrentStep(0);
      try {
        localStorage.setItem(TOUR_STORAGE_KEY, new Date().toISOString());
      } catch {
        // localStorage unavailable
      }
    }
  }, [currentStep, steps.length]);

  const prev = useCallback(() => {
    if (currentStep > 0) {
      setCurrentStep((s) => s - 1);
    }
  }, [currentStep]);

  const skip = useCallback(() => {
    setIsActive(false);
    setCurrentStep(0);
    try {
      localStorage.setItem(TOUR_STORAGE_KEY, new Date().toISOString());
    } catch {
      // localStorage unavailable
    }
  }, []);

  const restart = useCallback(() => {
    setCurrentStep(0);
    setIsActive(true);
    try {
      localStorage.removeItem(TOUR_STORAGE_KEY);
    } catch {
      // localStorage unavailable
    }
  }, []);

  return {
    isActive,
    currentStep,
    step: steps[currentStep],
    totalSteps: steps.length,
    isFirstStep: currentStep === 0,
    isLastStep: currentStep === steps.length - 1,
    next,
    prev,
    skip,
    restart,
  };
}
